import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  FormControlLabel,
  Checkbox,
  createTheme,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { getUserTypes } from "../../../../api/userType";
import { Autocomplete } from "@material-ui/lab";
import { getCursoById, updateCurso } from "../../../../api/curso";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

// Create theme for delete button (red)
const theme = createTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyPrice() {
  return {
    userType: "",
    value: 0,
    info: "0 €",
    active: true,
  };
}

export default function EditPricePage() {
  const [price, setPrice] = useState(getEmptyPrice());
  const [curso, setCurso] = useState({});
  const [userTypes, setUserTypes] = useState([]);
  const [selectedUserType, setSelectedUserType] = useState({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const priceId = useParams().id;
  const cursoId = useParams().cursoId;
  const history = useHistory();

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Obtener userTypes primero
        const resUserTypes = await getUserTypes();
        let userTypesData = [];
        if (resUserTypes.status === 200) {
          userTypesData = resUserTypes.data;
          setUserTypes(userTypesData);
        }

        // Luego obtener el curso
        const res = await getCursoById(cursoId);
        if (res.status === 200) {
          setCurso(res.data);

          // Si estamos editando un precio existente
          if (priceId) {
            const foundPrice = res.data.pricing?.find((x) => x._id === priceId);
            if (foundPrice) {
              setPrice(foundPrice);

              // Obtener el userType del precio (puede venir como string ID o como objeto populado)
              const priceUserTypeId =
                typeof foundPrice.userType === "object" &&
                foundPrice.userType !== null
                  ? foundPrice.userType._id || foundPrice.userType
                  : foundPrice.userType;

              // Buscar el userType en la lista de userTypes
              if (priceUserTypeId && userTypesData.length > 0) {
                const foundUserType = userTypesData.find((x) => {
                  const userTypeId = x._id?.toString();
                  const priceUserTypeIdStr = priceUserTypeId?.toString();
                  return userTypeId === priceUserTypeIdStr;
                });

                if (foundUserType) {
                  setSelectedUserType(foundUserType);
                } else {
                  console.warn("UserType no encontrado para el precio:", {
                    priceUserTypeId,
                    availableUserTypes: userTypesData.map((ut) => ({
                      id: ut._id,
                      titulo: ut.titulo,
                    })),
                  });
                }
              }
            }
          }
        }

        disableLoadingData();
      } catch (error) {
        console.error("Error cargando datos:", error);
        alertError({
          error: error,
          customMessage: "Could not get price.",
        });
        history.push("/cursos");
      }
    };
    fetchData();
  }, [cursoId, priceId, disableLoadingData, history]);

  // Efecto adicional para sincronizar selectedUserType cuando cambian los datos
  useEffect(() => {
    if (
      priceId &&
      price?.userType &&
      userTypes.length > 0 &&
      !selectedUserType?._id
    ) {
      // Obtener el userType del precio (puede venir como string ID o como objeto populado)
      const priceUserTypeId =
        typeof price.userType === "object" && price.userType !== null
          ? price.userType._id || price.userType
          : price.userType;

      if (priceUserTypeId) {
        const foundUserType = userTypes.find((x) => {
          const userTypeId = x._id?.toString();
          const priceUserTypeIdStr = priceUserTypeId?.toString();
          return userTypeId === priceUserTypeIdStr;
        });

        if (foundUserType) {
          setSelectedUserType(foundUserType);
        }
      }
    }
  }, [price, userTypes, priceId, selectedUserType]);

  function savePrice() {
    let saveCurso = curso;
    let savePrice = price;
    savePrice.userType = selectedUserType._id;
    if (priceId) {
      saveCurso = {
        ...saveCurso,
        pricing: saveCurso.pricing.map((item) =>
          item._id === priceId ? savePrice : item,
        ),
      };
    } else {
      if (!Array.isArray(saveCurso.pricing)) {
        saveCurso.pricing = [];
      }
      saveCurso.pricing.push(savePrice);
    }
    updateCurso(cursoId, saveCurso)
      .then((res) => {
        if (res.status === 200) {
          alertSuccess({
            title: "Saved!",
            customMessage: "Changes successfully saved.",
          });
          history.push("/edit-curso/" + cursoId);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "Could not save changes.",
        });
      });
  }

  const handleChange = (element) => (event) => {
    setPrice({ ...price, [element]: event.target.value });
  };

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Botones Save y Delete */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>

                  {showDelete && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: () => history.push("/edit-curso/" + cursoId),
            onSave: savePrice,
            onDelete: () => setOpenConfirmDialog(true),
            showDelete: !!priceId,
            setObj: setPrice,
            obj: price,
          })}
          <CardHeader title="Editar Precio"></CardHeader>
          <CardBody>
            <div className="d-flex">
              <div className="col-md-4">
                <Autocomplete
                  className="w-100 mr-5"
                  id="autocomplete-familia"
                  filterSelectedOptions
                  options={userTypes}
                  getOptionLabel={(option) => option?.titulo}
                  value={selectedUserType || ""}
                  onChange={(event, selected) => {
                    setSelectedUserType(selected);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Seleccionar Tipo de usuario"
                    />
                  )}
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`value`}
                  label="Precio"
                  value={price.value}
                  onChange={handleChange("value")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`info`}
                  label="Etiqueta Informativa"
                  value={price.info}
                  onChange={handleChange("info")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div style={{ marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={price.active || false}
                    onChange={() =>
                      setPrice({ ...price, active: !price.active })
                    }
                    name="checkActive"
                  />
                }
                label="Active"
              />
            </div>
          </CardBody>
        </Card>
        {priceId && (
          <>
            <ConfirmDialog
              title={"Are you sure you want to delete this price?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                let saveCurso = {
                  ...curso,
                  pricing: curso?.pricing?.filter((x) => x._id !== priceId),
                };
                updateCurso(cursoId, saveCurso)
                  .then((res) => {
                    if (res.status === 200) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "Price deleted",
                      });
                      history.push("/edit-curso/" + cursoId);
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not save changes.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
